import React, { FC } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Button, Chip, FormControlLabel, Grid, ListSubheader, Theme, Typography } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/DoneAll';

import IOSSwitch from 'components/IOSSwitch';

import { NOTIFICATION_FILTER_LABELS, NotificationTypeFilter } from '../constants';

interface Props {
  selectedFilter: NotificationTypeFilter;
  /** Outstanding items per filter, so an admin can see where the work is before clicking. */
  counts: Record<NotificationTypeFilter, number>;
  unreadCount: number;
  showResolved: boolean;
  onFilterChange: (filter: NotificationTypeFilter) => void;
  onShowResolvedChange: (showResolved: boolean) => void;
  onMarkAllClick: () => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  headerGrid: { color: '#000000', background: '#F5F8FA', padding: theme.spacing(1.5, 2) },
  subtitle: { marginLeft: theme.spacing(1.5), color: theme.palette.text.secondary },
  markButton: { padding: theme.spacing(0) },
  filters: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1.5, 1.25),
    background: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  chip: { height: 28 },
  chipCount: { fontWeight: theme.typography.fontWeightMedium, fontVariantNumeric: 'tabular-nums' },
  resolvedToggle: { marginLeft: 'auto', marginRight: 0 },
  resolvedLabel: { fontSize: 13, color: theme.palette.text.secondary, whiteSpace: 'nowrap' }
}));

const NotificationHeader: FC<Props> = props => {
  const classes = useStyles(props);
  const { selectedFilter, counts, unreadCount, showResolved, onFilterChange, onShowResolvedChange, onMarkAllClick } = props;

  const outstanding = counts[NotificationTypeFilter.ALL];

  return (
    <ListSubheader disableGutters>
      <Grid container direction='row' justify='space-between' alignItems='center' className={classes.headerGrid}>
        <Grid item>
          <Typography variant='h5' component='span'>
            Notifications
          </Typography>
          <Typography variant='body2' component='span' className={classes.subtitle}>
            {outstanding > 0 ? `${outstanding} need action` : 'Nothing outstanding'}
          </Typography>
        </Grid>
        <Grid item>
          <Button
            size='small'
            color='primary'
            className={classes.markButton}
            startIcon={<CheckIcon />}
            onClick={onMarkAllClick}
            disabled={unreadCount === 0}
            disableRipple
          >
            Mark all as read
          </Button>
        </Grid>
      </Grid>

      <div className={classes.filters}>
        {NOTIFICATION_FILTER_LABELS.map(({ filter, label }) => (
          <Chip
            key={filter}
            size='small'
            className={classes.chip}
            clickable
            color={filter === selectedFilter ? 'primary' : 'default'}
            variant={filter === selectedFilter ? 'default' : 'outlined'}
            onClick={() => onFilterChange(filter)}
            label={
              <span>
                {label} <span className={classes.chipCount}>{counts[filter]}</span>
              </span>
            }
          />
        ))}

        <FormControlLabel
          className={classes.resolvedToggle}
          classes={{ label: classes.resolvedLabel }}
          control={<IOSSwitch checked={showResolved} onChange={(_e, checked) => onShowResolvedChange(checked)} />}
          label='Resolved'
        />
      </div>
    </ListSubheader>
  );
};

export default NotificationHeader;
